import * as Sentry from '@sentry/react';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAuthUserContext,
  useCurrentUserFromLocalStorageContext,
  useDomainContext,
  useEnvironmentServiceContext,
  useExecutorRoleFromLocalStorageContext,
  useLocalStorageDomainServiceContext,
  useSimulatedRoleContext,
} from '../../../../context/contexts';
import { GraphQLError } from '../../../errors';
import FallbackComponent from '../pages/Fallback';
import { ErrorTypes } from '../types';
import { getSentryPanalytTags } from '../utils';
import { dialogCommonOptions } from './display-utils';

export const DomainScopedErrorBoundary = memo(
  (props: {
    children?: React.ReactNode;
    tags?: Record<string, string | null>;
  }) => {
    console.log2('DomainScopedErrorBoundary start');
    const environmentService = useEnvironmentServiceContext();
    const { t } = useTranslation();

    const domainService = useLocalStorageDomainServiceContext();
    const domain = useDomainContext();
    const domainFromLocalStorage = domainService.get();
    const currentUser = useAuthUserContext();
    const currentUserFromLocalStorage = useCurrentUserFromLocalStorageContext();
    const subDomain = environmentService.getSubDomain();
    const executorRoleFromLocalStorage =
      useExecutorRoleFromLocalStorageContext();
    const simulatedRole = useSimulatedRoleContext();

    const user = currentUser ?? currentUserFromLocalStorage;
    const userInfo = user
      ? {
          email: user.email ?? 'UNKNOWN',
          username: user.displayName ?? 'UNKNOWN',
          name: user.displayName ?? 'UNKNOWN',
        }
      : {};

    const dialogOptions = {
      ...dialogCommonOptions(t),
      user: {
        email: userInfo.email,
        name: userInfo.name,
      },
    };

    const panalytTags = getSentryPanalytTags({
      subDomain,
      domain,
      domainFromLocalStorage,
      executorRole: null,
      executorRoleFromLocalStorage,
      simulatedRole,
      currentUser,
      currentUserFromLocalStorage,
      errorType: ErrorTypes.ERROR_BOUNDARY,
    });

    console.log2('DomainScopedErrorBoundary end');
    return (
      <Sentry.ErrorBoundary
        beforeCapture={(scope, error) => {
          scope.setUser({
            ...scope.getUser(),
            ...userInfo,
          });
          let errorTags: Record<string, string> = {};
          if (error instanceof GraphQLError) {
            errorTags = {
              ...(error.tags ?? {}),
              errorType: error.errorType ?? ErrorTypes.ERROR_BOUNDARY,
            };
          }
          scope.setTags({
            ...(props.tags ?? {}),
            ...panalytTags,
            ...errorTags,
          });
        }}
        fallback={(errorData: {
          error: unknown;
          // componentStack: string;
          // eventId: string;
          resetError(): void;
        }) => {
          console.debug('DomainScopedErrorBoundary hit!', errorData);
          return (
            <FallbackComponent
              error={errorData.error as Error}
              resetError={() => errorData.resetError()}
            />
          );
        }}
        showDialog
        dialogOptions={dialogOptions}
      >
        {props.children}
      </Sentry.ErrorBoundary>
    );
  }
);
DomainScopedErrorBoundary.displayName = 'DomainScopedErrorBoundary';
